import { defaultFeatures, type AppConfig } from '@ms-cloudpack/common-types';
import type { ReadAppConfigOptions } from './readAppConfig.js';

/**
 * Update the app config read from disk with extra settings that were passed in from CLI options.
 * This is called after any extended configs have been merged.
 * @returns The original `config` object for convenience (modifications are done in-place)
 */
export function augmentAppConfig<TConfig extends Pick<AppConfig, 'packageSettings' | 'features' | 'define' | 'server'>>(
  config: TConfig,
  options: ReadAppConfigOptions,
): TConfig {
  const { extraPackageSettings, extraFeatures, extraDefineFlags, enableDefaultFeatures, extraServerConfig } = options;

  if (extraPackageSettings) {
    // Add these last so they take precedence.
    (config.packageSettings ??= []).push(...extraPackageSettings);
  }

  if (extraFeatures) {
    // Merge extra features
    config.features = { ...config.features, ...extraFeatures };
  }

  if (extraDefineFlags) {
    config.define = { ...config.define, ...extraDefineFlags };
  }

  if (enableDefaultFeatures) {
    // Enable select features by default.
    // User-provided features should override the defaults.
    config.features = { ...defaultFeatures, ...config.features };
  }

  if (extraServerConfig) {
    // Only set values that aren't undefined, to avoid overwriting values from the config
    // in case a key was included in extraServerConfig by default without a value
    for (const [key, value] of Object.entries(extraServerConfig)) {
      if (value !== undefined) {
        config.server ??= {};
        (config.server as Record<string, unknown>)[key] = value;
      }
    }
  }

  return config;
}
